var __extends = this && this.__extends || function () {
  var _extendStatics = function extendStatics(d, b) {
    _extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics(d, b);
  };

  return function (d, b) {
    _extendStatics(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

import * as React from 'react';
import * as PropTypes from 'prop-types';
import { classNames, Keys, guid } from '@progress/kendo-react-common';
import { FloatingLabel } from '@progress/kendo-react-labels';
import ListContainer from '../common/ListContainer';
import List from '../common/List';
import TagList from './TagList';
import SearchBar from '../common/SearchBar';
import DropDownBase from '../common/DropDownBase';
import { ActiveDescendant } from './../common/settings';
import { itemIndexStartsWith, getItemValue, areSame, matchDataCollections, removeDataItems, isPresent, preventDefaultNonInputs } from '../common/utils';
import { validatePackage } from '@progress/kendo-licensing';
import { packageMetadata } from '../package-metadata';
var VALIDATION_MESSAGE = 'Please enter a valid value!';

var preventDefault = function preventDefault(event) {
  return event.preventDefault();
};

var matchTags = function matchTags(tag1, tag2, key) {
  if (!!tag1 !== !!tag2 || tag1.text !== tag2.text) {
    return false;
  }

  return tag1 === tag2 || matchDataCollections(tag1.data, tag2.data, key);
};

var isCustom = function isCustom(type) {
  return type === FocusedItemType.CustomItem;
};

var FocusedItemType;

(function (FocusedItemType) {
  FocusedItemType[FocusedItemType["None"] = 0] = "None";
  FocusedItemType[FocusedItemType["ListItem"] = 1] = "ListItem";
  FocusedItemType[FocusedItemType["CustomItem"] = 2] = "CustomItem";
})(FocusedItemType || (FocusedItemType = {}));
/**
 * Represents the [KendoReact MultiSelect component]({% slug overview_multiselect %}).
 *
 * @example
 * ```jsx
 * class App extends React.Component {
 *    multiselect = null;
 *    render() {
 *       return (
 *          <div>
 *              <MultiSelect
 *                  data={[ "Albania", "Andorra", "Austria", "Belarus" ]}
 *                  ref={component => this.multiselect = component}
 *              />
 *              <button onClick={() => alert(this.multiselect.value)}>alert value</button>
 *          </div>
 *       );
 *    }
 * }
 * ReactDOM.render(<App />, document.querySelector('my-app'));
 * ```
 */


var MultiSelect = function (_super) {
  __extends(MultiSelect, _super);

  function MultiSelect(props) {
    var _this = _super.call(this, props) || this;
    /**
     * @hidden
     */


    _this.state = {
      activedescendant: ActiveDescendant.PopupList
    };
    _this._valueItemsDuringOnChange = null;
    _this._inputId = guid();
    _this.base = new DropDownBase(_this);
    _this._tags = [];
    _this._skipFocusEvent = false;
    _this.scrollToFocused = false;
    /**
     * @hidden
     */

    _this.focus = function () {
      if (_this._input) {
        _this._input.focus();
      }
    };
    /**
     * @hidden
     */


    _this.handleItemSelect = function (index, state) {
      var _a = _this.props,
          _b = _a.data,
          data = _b === void 0 ? [] : _b,
          dataItemKey = _a.dataItemKey,
          virtual = _a.virtual;
      var skip = virtual ? virtual.skip : 0;
      var dataItem = data[index - skip];

      var indexInValue = _this.value.findIndex(function (i) {
        return areSame(i, dataItem, dataItemKey);
      });

      var newItems = [];

      if (indexInValue !== -1) {
        // item is already selected
        newItems = _this.value;
        newItems.splice(indexInValue, 1);
      } else {
        newItems = _this.value.concat([dataItem]);
      }

      var text = _this.props.filter !== undefined ? _this.props.filter : _this.state.text;

      if (text) {
        if (_this.state.text) {
          state.data.text = '';
        }

        _this.base.filterChanged('', state);
      }

      if (_this.state.focusedIndex !== undefined) {
        state.data.focusedIndex = undefined;
      }

      _this.triggerOnChange(newItems, state);

      _this.base.triggerPageChangeCornerItems(dataItem, state);
    };
    /**
     * @hidden
     */


    _this.onTagDelete = function (itemsToRemove, event) {
      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;

      var state = _this.base.initState();

      state.syntheticEvent = event;

      if (opened) {
        _this.base.togglePopup(state);
      }

      if (!_this.state.focused) {
        state.data.focused = true;
      }

      var selected = _this.value;
      removeDataItems(selected, itemsToRemove, _this.props.dataItemKey);

      _this.triggerOnChange(selected, state);

      _this.applyState(state);
    };
    /**
     * @hidden
     */


    _this.itemFocus = function (index, state) {
      var _a = _this.props,
          _b = _a.data,
          data = _b === void 0 ? [] : _b,
          allowCustom = _a.allowCustom,
          virtual = _a.virtual;
      var skip = virtual ? virtual.skip : 0;
      var text = _this.props.filter !== undefined ? _this.props.filter : _this.state.text;

      var focusedIndex = _this.getFocusedState().focusedIndex;

      var customItem = allowCustom && text;
      var nextFocusedItem = data[index - skip];

      if (nextFocusedItem && focusedIndex !== index) {
        if (_this.state.focusedIndex !== index) {
          state.data.focusedIndex = index;
          state.data.activedescendant = ActiveDescendant.PopupList;
        }
      } else if (customItem && index === -1) {
        if (_this.state.focusedIndex !== undefined) {
          state.data.focusedIndex = undefined;
        }
      }

      _this.base.triggerPageChangeCornerItems(nextFocusedItem, state);
    };

    _this.searchbarRef = function (searchbar) {
      var input = _this._input = searchbar && searchbar.input;

      if (input && _this.state.focused) {
        setTimeout(function () {
          return input.focus();
        }, 0);
      }
    };

    _this.onChangeHandler = function (event) {
      var state = _this.base.initState();

      var value = event.currentTarget.value;
      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;
      state.syntheticEvent = event;

      if (_this.props.filter === undefined) {
        state.data.text = value;
      }

      state.data.focusedIndex = undefined;

      if (!opened) {
        _this.base.togglePopup(state);
      }

      _this.base.filterChanged(value, state);

      _this.applyState(state);
    };

    _this.clearButtonClick = function (event) {
      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;

      var state = _this.base.initState();

      state.syntheticEvent = event;
      event.stopPropagation();

      if (_this.value.length > 0) {
        _this.triggerOnChange([], state);
      }

      if (_this.state.focusedIndex !== undefined) {
        state.data.focusedIndex = undefined;
      }

      if (opened) {
        _this.base.togglePopup(state);
      }

      var text = _this.props.filter !== undefined ? _this.props.filter : _this.state.text;

      if (isPresent(text) && text !== '') {
        _this.base.filterChanged('', state);
      }

      if (_this.state.text) {
        state.data.text = '';
      }

      _this.applyState(state);
    };

    _this.onInputKeyDown = function (event) {
      var keyCode = event.keyCode;
      var text = _this.props.filter !== undefined ? _this.props.filter : _this.state.text;
      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;

      var focusedItem = _this.getFocusedState().focusedItem;

      var state = _this.base.initState();

      state.syntheticEvent = event;

      if (!text && _this.value.length > 0 && (keyCode === Keys.left || keyCode === Keys.right || keyCode === Keys.home || keyCode === Keys.end || keyCode === Keys.delete || keyCode === Keys.backspace)) {
        return _this.onTagsNavigate(event, state);
      }

      var togglePopup = function togglePopup() {
        event.preventDefault();

        _this.base.togglePopup(state);

        _this.applyState(state);
      };

      if (opened) {
        if (event.altKey && keyCode === Keys.up) {
          togglePopup();
        } else if (keyCode === Keys.up || keyCode === Keys.down) {
          event.preventDefault();

          _this.onNavigate(state, keyCode);
        } else if (keyCode === Keys.enter) {
          event.preventDefault();

          if (_this.props.allowCustom && text && focusedItem === null) {
            _this.customItemSelect(event);
          } else {
            _this.selectFocusedItem(event);
          }
        } else if (keyCode === Keys.esc) {
          togglePopup();
        }
      } else if (event.altKey && keyCode === Keys.down) {
        togglePopup();
      }
    };

    _this.renderListContainer = function () {
      var base = _this.base;
      var _a = _this.props,
          header = _a.header,
          footer = _a.footer,
          allowCustom = _a.allowCustom,
          dir = _a.dir,
          _b = _a.data,
          data = _b === void 0 ? [] : _b;

      var popupSettings = _this.base.getPopupSettings();

      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;
      var text = _this.props.filter !== undefined ? _this.props.filter : _this.state.text;
      var virtual = _this.props.virtual !== undefined;
      var popupWidth = popupSettings.width !== undefined ? popupSettings.width : base.popupWidth;
      var listContainerProps = {
        dir: dir !== undefined ? dir : base.dirCalculated,
        width: popupWidth,
        popupSettings: {
          className: classNames('k-list-container k-reset', popupSettings.className),
          animate: popupSettings.animate,
          anchor: _this.element,
          show: opened,
          onOpen: _this.onPopupOpened,
          onClose: _this.onPopupClosed,
          appendTo: popupSettings.appendTo
        },
        itemsCount: data.length
      };

      var focusedType = _this.getFocusedState().focusedType;

      var customItem = allowCustom && text && React.createElement("div", {
        className: "k-list",
        key: "customitem",
        onClick: _this.customItemSelect
      }, React.createElement("div", {
        className: classNames('k-item k-custom-item', {
          'k-state-focused': isCustom(focusedType)
        })
      }, text, React.createElement("span", {
        className: "k-icon k-i-plus",
        style: {
          float: 'right'
        }
      })));
      return React.createElement(ListContainer, __assign({}, listContainerProps), virtual && header, _this.base.renderScrollWrapper(React.createElement(React.Fragment, null, !virtual && header, customItem, _this.renderList(), !virtual && footer, _this.base.renderScrollElement())), virtual && footer);
    };

    _this.renderList = function () {
      var _a = _this.props,
          _b = _a.data,
          data = _b === void 0 ? [] : _b,
          textField = _a.textField,
          listNoDataRender = _a.listNoDataRender,
          itemRender = _a.itemRender,
          dataItemKey = _a.dataItemKey,
          virtual = _a.virtual;
      var vs = _this.base.vs;
      var skip = virtual ? virtual.skip : 0;
      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;

      var focusedIndex = _this.getFocusedState().focusedIndex;

      var popupSettings = _this.base.getPopupSettings();

      var translate = "translateY(" + vs.translate + "px)";
      return React.createElement(List, {
        id: _this.base.listBoxId,
        show: opened,
        data: data.slice(),
        focusedIndex: focusedIndex - skip,
        value: _this.value,
        textField: textField,
        valueField: dataItemKey,
        optionsGuid: _this.base.guid,
        listRef: function listRef(list) {
          return vs.list = _this.base.list = list;
        },
        wrapperStyle: vs.enabled ? {
          float: 'left',
          width: '100%'
        } : {
          maxHeight: popupSettings.height
        },
        wrapperCssClass: vs.enabled ? undefined : 'k-list-scroller',
        listStyle: vs.enabled ? {
          transform: translate
        } : undefined,
        key: "listKey",
        skip: skip,
        onClick: _this.handleItemClick,
        itemRender: itemRender,
        noDataRender: listNoDataRender,
        onMouseDown: preventDefault,
        onBlur: _this.handleBlur
      });
    };

    _this.customItemSelect = function (event) {
      var _a;

      var itemText = _this.props.filter !== undefined ? _this.props.filter : _this.state.text;
      var textField = _this.props.textField;

      if (!itemText) {
        return;
      }

      var state = _this.base.initState();

      state.syntheticEvent = event;
      var item = textField ? (_a = {}, _a[textField] = itemText, _a) : itemText;

      if (_this.state.text !== undefined) {
        state.data.text = '';
      }

      state.data.focusedIndex = undefined;

      _this.base.filterChanged('', state);

      var newItems = _this.value.concat([item]);

      _this.triggerOnChange(newItems, state);

      _this.base.togglePopup(state);

      _this.applyState(state);
    };

    _this.handleWrapperClick = function (event) {
      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;
      var input = _this._input;

      if (!opened && input) {
        _this.focusElement(input);
      }

      var state = _this.base.initState();

      state.syntheticEvent = event;

      if (!_this.state.focused) {
        state.events.push({
          type: 'onFocus'
        });
        state.data.focused = true;
      }

      _this.base.togglePopup(state);

      _this.applyState(state);
    };

    _this.handleItemClick = function (index, event) {
      var state = _this.base.initState();

      state.syntheticEvent = event;

      _this.handleItemSelect(index, state);

      if (_this.props.autoClose) {
        _this.base.togglePopup(state);
      }

      _this.applyState(state);
    };

    _this.handleBlur = function (event) {
      if (!_this.state.focused || _this._skipFocusEvent) {
        return;
      }

      var opened = _this.props.opened !== undefined ? _this.props.opened : _this.state.opened;

      var state = _this.base.initState();

      var _a = _this.props,
          allowCustom = _a.allowCustom,
          filterable = _a.filterable;
      state.syntheticEvent = event;
      state.data.focused = false;
      state.events.push({
        type: 'onBlur'
      });

      if (opened) {
        if (_this.state.opened) {
          state.data.opened = false;
        }

        state.events.push({
          type: 'onClose'
        });
      }

      if (!allowCustom && !filterable && _this.state.text) {
        state.data.text = '';
      }

      _this.applyState(state);
    };

    _this.handleFocus = function (event) {
      if (_this._skipFocusEvent) {
        return;
      }

      _this.base.handleFocus(event);
    };

    _this.onPopupOpened = function () {
      if (_this._input && _this.state.focused) {
        _this.focusElement(_this._input);
      }
    };

    _this.onPopupClosed = function () {
      if (_this.state.focused) {
        setTimeout(function () {
          if (_this.state.focused) {
            _this.focusElement(_this._input);
          }
        }, 0);
      }
    };

    _this.setValidity = function () {
      if (_this._input && _this._input.setCustomValidity) {
        _this._input.setCustomValidity(_this.validity.valid ? '' : _this.props.validationMessage || VALIDATION_MESSAGE);
      }
    };

    validatePackage(packageMetadata);
    return _this;
  }

  Object.defineProperty(MultiSelect.prototype, "element", {
    /**
     * @hidden
     */
    get: function get() {
      return this._element;
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(MultiSelect.prototype, "value", {
    /**
     * Represents the value of the MultiSelect.
     */
    get: function get() {
      var result = [];

      if (this._valueItemsDuringOnChange) {
        result.push.apply(result, this._valueItemsDuringOnChange);
      } else if (this.props.value) {
        result.push.apply(result, this.props.value);
      } else if (this.state.value) {
        result.push.apply(result, this.state.value);
      } else if (this.props.defaultValue) {
        result.push.apply(result, this.props.defaultValue);
      }

      return result;
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(MultiSelect.prototype, "name", {
    /**
     * Gets the `name` property of the MultiSelect.
     */
    get: function get() {
      return this.props.name;
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(MultiSelect.prototype, "validity", {
    /**
     * Represents the validity state into which the MultiSelect is set.
     */
    get: function get() {
      var customError = this.props.validationMessage !== undefined;
      var isValid = !this.required || this.value !== null && this.value.length > 0 && this.value !== undefined;
      var valid = this.props.valid !== undefined ? this.props.valid : isValid;
      return {
        customError: customError,
        valid: valid,
        valueMissing: this.value === null
      };
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(MultiSelect.prototype, "required", {
    /**
     * @hidden
     */
    get: function get() {
      return this.props.required !== undefined ? this.props.required : MultiSelect.defaultProps.required;
    },
    enumerable: true,
    configurable: true
  });
  Object.defineProperty(MultiSelect.prototype, "validityStyles", {
    get: function get() {
      return this.props.validityStyles !== undefined ? this.props.validityStyles : MultiSelect.defaultProps.validityStyles;
    },
    enumerable: true,
    configurable: true
  });
  /**
   * @hidden
   */

  MultiSelect.prototype.componentDidUpdate = function (prevProps, prevState) {
    var virtual = this.props.virtual;
    var skip = virtual ? virtual.skip : 0;
    var prevTotal = prevProps.virtual ? prevProps.virtual.total : 0;
    var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
    var prevOpened = prevProps.opened !== undefined ? prevProps.opened : prevState.opened;
    var opening = !prevOpened && opened;
    var closing = prevOpened && !opened;
    var popupSettings = this.base.getPopupSettings();

    if (!popupSettings.animate && closing) {
      this.onPopupClosed();
    }

    if (virtual && virtual.total !== prevTotal) {
      this.base.vs.calcScrollElementHeight();
      this.base.vs.reset();
    } else {
      var _a = this.getFocusedState(),
          focusedItem = _a.focusedItem,
          focusedIndex = _a.focusedIndex;

      if (opening && virtual) {
        this.base.scrollToVirtualItem(virtual, focusedIndex - skip);
      } else if (opening && !virtual) {
        this.base.scrollToItem(focusedIndex);
      } else if (opened && prevOpened && focusedItem && this.scrollToFocused) {
        this.base.scrollToItem(focusedIndex - skip);
      }
    }

    this.scrollToFocused = false;
    this.setValidity();
  };
  /**
   * @hidden
   */


  MultiSelect.prototype.componentDidMount = function () {
    this.base.didMount();
    this.setValidity();
  };
  /**
   * @hidden
   */


  MultiSelect.prototype.onNavigate = function (state, keyCode) {
    var _a = this.props,
        allowCustom = _a.allowCustom,
        _b = _a.data,
        data = _b === void 0 ? [] : _b;
    var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
    var text = this.props.filter !== undefined ? this.props.filter : this.state.text;

    var _c = this.getFocusedState(),
        focusedType = _c.focusedType,
        focusedIndex = _c.focusedIndex;

    var customItem = allowCustom && text;
    var customItemFocused = isCustom(focusedType);
    var base = this.base;
    var vs = base.vs;

    if (opened && keyCode === Keys.up && customItemFocused) {
      if (this.state.focusedIndex !== undefined) {
        state.data.focusedIndex = undefined;
      }
    } else {
      var newFocused = base.navigation.navigate({
        keyCode: keyCode,
        current: focusedIndex,
        max: (vs.enabled ? vs.total : data.length) - 1,
        min: customItem ? -1 : 0
      });

      if (newFocused !== undefined) {
        this.itemFocus(newFocused, state);
        this.scrollToFocused = true;
      }
    }

    this.applyState(state);
  };
  /**
   * @hidden
   */


  MultiSelect.prototype.render = function () {
    var _this = this;

    var _a = this.props,
        style = _a.style,
        className = _a.className,
        label = _a.label,
        dir = _a.dir,
        disabled = _a.disabled,
        tags = _a.tags,
        textField = _a.textField,
        dataItemKey = _a.dataItemKey,
        virtual = _a.virtual;
    var _b = this.state,
        focused = _b.focused,
        text = _b.text,
        focusedTag = _b.focusedTag;
    var vs = this.base.vs;
    var id = this.props.id || this._inputId;
    vs.enabled = virtual !== undefined;

    if (virtual !== undefined) {
      vs.skip = virtual.skip;
      vs.total = virtual.total;
      vs.pageSize = virtual.pageSize;
    }

    var tagsToRender = [];

    if (tags === undefined) {
      this.value.forEach(function (item) {
        tagsToRender.push({
          text: getItemValue(item, textField),
          data: [item]
        });
      });
    } else {
      tagsToRender.push.apply(tagsToRender, tags);
    }

    this.setItems(tagsToRender, this._tags);
    var isValid = !this.validityStyles || this.validity.valid;
    var component = React.createElement("span", {
      ref: function ref(dropdown) {
        return _this._element = dropdown;
      },
      className: classNames('k-widget k-multiselect k-header', className, {
        'k-state-focused': focused && !disabled,
        'k-state-invalid': !isValid,
        'k-state-disabled': disabled
      }),
      style: !label ? style : __assign({}, style, {
        width: undefined
      }),
      dir: dir,
      onFocus: this.handleFocus,
      onBlur: this.handleBlur
    }, React.createElement("div", {
      onClick: this.handleWrapperClick,
      ref: function ref(wrapper) {
        return _this.base.wrapper = wrapper;
      },
      className: "k-multiselect-wrap k-floatwrap",
      onMouseDown: preventDefaultNonInputs
    }, tagsToRender.length > 0 && React.createElement(TagList, {
      tagRender: this.props.tagRender,
      onTagDelete: this.onTagDelete,
      data: tagsToRender,
      guid: this.base.guid,
      focused: focusedTag ? tagsToRender.find(function (t) {
        return matchTags(t, focusedTag, dataItemKey);
      }) : undefined
    }), this.renderSearchbar(id), this.props.loading && React.createElement("span", {
      className: "k-icon k-i-loading"
    })), this.renderListContainer());
    return label ? React.createElement(FloatingLabel, {
      label: label,
      editorId: id,
      editorValue: text || getItemValue(this.value[0], textField),
      editorValid: isValid,
      editorDisabled: disabled,
      style: {
        width: style ? style.width : undefined
      },
      children: component
    }) : component;
  };

  MultiSelect.prototype.renderSearchbar = function (id) {
    var _a = this.state,
        activedescendant = _a.activedescendant,
        focusedTag = _a.focusedTag;
    var _b = this.props,
        disabled = _b.disabled,
        placeholder = _b.placeholder;
    var text = (this.props.filter !== undefined ? this.props.filter : this.state.text) || '';
    var opened = this.props.opened !== undefined ? this.props.opened : this.state.opened;
    var focusedIndex = this.getFocusedState().focusedIndex;
    var clearButton = !!text || this.value.length > 0;
    var placeholderToShow = this.value.length === 0 && !text ? placeholder : undefined;
    var ariaActivedescendant = activedescendant === ActiveDescendant.TagsList && focusedTag !== undefined ? "tag-" + this.base.guid + "-" + focusedTag.text.replace(/\s+/g, '-') : "option-" + this.base.guid + "-" + focusedIndex;
    return React.createElement(SearchBar, {
      id: id,
      size: Math.max((placeholderToShow || '').length, text.length, 1),
      tabIndex: this.props.tabIndex,
      accessKey: this.props.accessKey,
      placeholder: placeholderToShow,
      value: text,
      onChange: this.onChangeHandler,
      onKeyDown: this.onInputKeyDown,
      ref: this.searchbarRef,
      disabled: disabled,
      expanded: opened,
      owns: this.base.listBoxId,
      activedescendant: ariaActivedescendant,
      ariaDescribedBy: "tagslist-" + this.base.guid + " " + (this.props.ariaDescribedBy || ''),
      clearButton: clearButton,
      clearButtonClick: this.clearButtonClick,
      ariaLabelledBy: this.props.ariaLabelledBy
    });
  };

  MultiSelect.prototype.onTagsNavigate = function (event, state) {
    var keyCode = event.keyCode;
    var focusedTag = this.state.focusedTag;
    var tags = this._tags;
    var itemsKey = this.props.dataItemKey;
    var focusedIndex = focusedTag ? tags.findIndex(function (t) {
      return matchTags(t, focusedTag, itemsKey);
    }) : -1;
    var newFocusedTag = undefined;
    var hasFocused = focusedIndex !== -1;

    if (keyCode === Keys.left) {
      if (hasFocused) {
        focusedIndex = Math.max(0, focusedIndex - 1);
      } else {
        focusedIndex = tags.length - 1;
      }

      newFocusedTag = tags[focusedIndex];
    } else if (keyCode === Keys.right) {
      if (focusedIndex === tags.length - 1) {
        newFocusedTag = undefined;
      } else if (hasFocused) {
        focusedIndex = Math.min(tags.length - 1, focusedIndex + 1);
        newFocusedTag = tags[focusedIndex];
      }
    } else if (keyCode === Keys.home) {
      newFocusedTag = tags[0];
    } else if (keyCode === Keys.end) {
      newFocusedTag = tags[tags.length - 1];
    } else if (keyCode === Keys.delete) {
      if (hasFocused) {
        var items = this.value;
        removeDataItems(items, tags[focusedIndex].data, itemsKey);
        this.triggerOnChange(items, state);
      }
    } else if (keyCode === Keys.backspace) {
      var items = this.value;

      if (hasFocused) {
        removeDataItems(items, tags[focusedIndex].data, itemsKey);
        this.triggerOnChange(items, state);
      } else if (!hasFocused && tags.length) {
        var removed = tags.pop();
        removeDataItems(items, removed.data, itemsKey);
        this.triggerOnChange(items, state);
      }
    }

    if (newFocusedTag !== focusedTag) {
      state.data.focusedTag = newFocusedTag;
      state.data.activedescendant = ActiveDescendant.TagsList;
    }

    this.applyState(state);
  };

  MultiSelect.prototype.triggerOnChange = function (items, state) {
    if (this.props.value === undefined) {
      state.data.value = items.slice();
    }

    this._valueItemsDuringOnChange = [];
    this.setItems(items, this._valueItemsDuringOnChange);
    state.events.push({
      type: 'onChange'
    });
  };

  MultiSelect.prototype.selectFocusedItem = function (event) {
    var _a = this.props,
        _b = _a.data,
        data = _b === void 0 ? [] : _b,
        virtual = _a.virtual;
    var focusedIndex = this.getFocusedState().focusedIndex;
    var skip = virtual ? virtual.skip : 0;

    if (data[focusedIndex - skip] !== undefined) {
      this.handleItemClick(focusedIndex, event);
    }
  };

  MultiSelect.prototype.setItems = function (srcItems, destItems) {
    destItems.length = 0;
    destItems.push.apply(destItems, srcItems);
  };

  MultiSelect.prototype.getFocusedState = function () {
    var focusedIndex = this.state.focusedIndex;
    var text = this.props.filter !== undefined ? this.props.filter : this.state.text;
    var _a = this.props,
        allowCustom = _a.allowCustom,
        _b = _a.data,
        data = _b === void 0 ? [] : _b,
        dataItemKey = _a.dataItemKey,
        virtual = _a.virtual,
        textField = _a.textField,
        _c = _a.focusedItemIndex,
        focusedItemIndex = _c === void 0 ? itemIndexStartsWith : _c;
    var skip = virtual && virtual.skip || 0;
    var focusedInd;

    if (focusedIndex !== undefined) {
      return {
        focusedIndex: focusedIndex,
        focusedItem: data[focusedIndex - skip],
        focusedType: FocusedItemType.ListItem
      };
    }

    var selected = this.value;

    if (allowCustom && text) {
      return {
        focusedItem: null,
        focusedIndex: -1,
        focusedType: FocusedItemType.CustomItem
      };
    } else if (text) {
      focusedInd = focusedItemIndex(data, text, textField);
      return {
        focusedItem: data[focusedInd],
        focusedIndex: focusedInd + skip,
        focusedType: FocusedItemType.ListItem
      };
    } else if (selected.length) {
      var last_1 = selected[selected.length - 1];
      focusedInd = data.findIndex(function (i) {
        return areSame(i, last_1, dataItemKey);
      });

      if (data[focusedInd] !== undefined) {
        return {
          focusedIndex: focusedInd + skip,
          focusedItem: data[focusedInd],
          focusedType: FocusedItemType.ListItem
        };
      }

      return {
        focusedType: FocusedItemType.None,
        focusedIndex: -1
      };
    }

    return skip === 0 ? {
      focusedItem: data[0],
      focusedIndex: 0,
      focusedType: FocusedItemType.ListItem
    } : {
      focusedType: FocusedItemType.None,
      focusedIndex: -1
    };
  };

  MultiSelect.prototype.focusElement = function (element) {
    var _this = this;

    this._skipFocusEvent = true;
    element.focus();
    setTimeout(function () {
      return _this._skipFocusEvent = false;
    }, 0);
  };

  MultiSelect.prototype.applyState = function (state) {
    this.base.applyState(state);
    this._valueItemsDuringOnChange = null;
  };
  /**
   * @hidden
   */


  MultiSelect.propTypes = __assign({}, DropDownBase.propTypes, {
    autoClose: PropTypes.bool,
    value: PropTypes.arrayOf(PropTypes.any),
    defaultValue: PropTypes.arrayOf(PropTypes.any),
    dataItemKey: PropTypes.string,
    placeholder: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.any)
    })),
    tagRender: PropTypes.func,
    id: PropTypes.string,
    ariaLabelledBy: PropTypes.string,
    ariaDescribedBy: PropTypes.string
  });
  /**
   * @hidden
   */

  MultiSelect.defaultProps = __assign({}, DropDownBase.defaultProps, {
    autoClose: true,
    required: false
  });
  return MultiSelect;
}(React.Component);

export default MultiSelect;